import type { PreUploadPageReport } from "@bidi-takeoff/shared/pdf-classify-page";

interface Props {
  page: PreUploadPageReport;
  detectedScaleHint: string | null;
}

const MODE_BADGE: Record<
  PreUploadPageReport["rasterMode"],
  { label: string; cls: string; symbol: string }
> = {
  vector: {
    label: "Vektor",
    cls: "bg-emerald-100 text-emerald-800 border-emerald-200",
    symbol: "✓",
  },
  raster: {
    label: "Raster",
    cls: "bg-amber-100 text-amber-800 border-amber-200",
    symbol: "⚠",
  },
  mixed: {
    label: "Mischform",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    symbol: "⚠",
  },
};

export default function PageReportCard({ page, detectedScaleHint }: Props) {
  const badge = MODE_BADGE[page.rasterMode];
  const isAutonomous =
    page.rasterMode === "vector" && page.hasOcgLayers && !!detectedScaleHint;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">
            Seite {page.index + 1}
          </div>
          <div className="text-sm font-mono text-slate-700 mt-0.5">
            {page.pageWidth.toFixed(0)} × {page.pageHeight.toFixed(0)} pt
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium border rounded px-2 py-0.5 ${badge.cls}`}
        >
          <span aria-hidden="true">{badge.symbol}</span>
          {badge.label}
        </span>
      </div>

      <dl className="text-xs text-slate-600 space-y-0.5 mb-2">
        <div className="flex justify-between">
          <dt>Pfad-Operatoren:</dt>
          <dd className="font-mono">{page.pathConstructCount}</dd>
        </div>
        <div className="flex justify-between">
          <dt>Bild-Streams:</dt>
          <dd className="font-mono">{page.imageStreamCount}</dd>
        </div>
        <div className="flex justify-between">
          <dt>OCG-Layer:</dt>
          <dd className={page.hasOcgLayers ? "text-emerald-700" : "text-amber-700"}>
            {page.hasOcgLayers ? "ja ✓" : "nein"}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt>Maßstab:</dt>
          <dd
            className={
              detectedScaleHint
                ? "text-emerald-700 font-mono"
                : "text-amber-700"
            }
          >
            {detectedScaleHint ?? "nicht im Plan-Text gefunden"}
          </dd>
        </div>
      </dl>

      {isAutonomous && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-2">
          Pipeline kann autonom durchlaufen.
        </div>
      )}

      {page.warnings.length > 0 && (
        <ul className="text-xs text-amber-800 space-y-1 mt-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {page.warnings.map((w, i) => (
            <li key={i} className="flex gap-1.5">
              <span aria-hidden="true" className="text-amber-600 mt-0.5">
                ›
              </span>
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
